/**
 * The dev-only impersonation control, loaded only where it exists.
 *
 * @module components/platform/useImpersonationAction
 */

import { useEffect, useState } from "react";

import type { UserSummary } from "@/platform";

type ImpersonationComponent = React.ComponentType<{ user: UserSummary }>;

/**
 * @returns The component in a dev build, and undefined everywhere else.
 *
 * @example
 * ```tsx
 * const ImpersonationAction = useImpersonationAction();
 * ```
 */
export function useImpersonationAction(): ImpersonationComponent | undefined {
  const [action, setAction] = useState<ImpersonationComponent | undefined>(undefined);

  useEffect(() => {
    let active = true;
    if (typeof __IMPERSONATION__ !== "undefined" && __IMPERSONATION__) {
      import("@/components/dev/ImpersonationAction")
        .then((mod) => {
          if (active) {
            setAction(() => mod.ImpersonationAction);
          }
        })
        .catch(() => {
          // Dev feature unavailable (e.g. a production build tree-shook it).
        });
    }
    return () => {
      active = false;
    };
  }, []);

  return action;
}
